import type { Column, RowData } from '@tanstack/table-core';
import { ArrowRightIcon, ArrowUpDown, FilterIcon } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TranslationKeys } from '@/lib/translations/types';

export const TableHeader = <T extends RowData>({
  column,
  type,
  headersTranslationPath,
  optionsTranslationPath,
}: {
  column: Column<T, string | number | boolean | undefined>;
  type: 'input' | 'select' | 'sort-only';
  headersTranslationPath: string;
  optionsTranslationPath?: string;
}): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);

  return (
    <div className="flex items-center">
      <FormattedMessage
        id={`${headersTranslationPath}.${column.id}.header` as TranslationKeys}
      />
      <Button
        variant="ghost"
        className="p-0 h-4 w-4 ml-1"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        <ArrowUpDown />
      </Button>
      {type === 'sort-only' ? null : (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" className="p-0 h-4 w-4 ml-1">
              <FilterIcon />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-2">
            {type === 'input' ? (
              <form
                className="relative flex items-center w-full"
                onSubmit={e => {
                  e.preventDefault();
                  column.setFilterValue(searchTerm);
                }}
              >
                <Input
                  onChange={e => setSearchTerm(e.target.value)}
                  defaultValue={
                    (column.getFilterValue() as string | undefined) ?? null!
                  }
                  placeholder="Search"
                  className="w-full"
                />
                <button className="absolute right-2 top-0 h-full" type="submit">
                  <ArrowRightIcon />
                </button>
              </form>
            ) : (
              <Select
                onValueChange={column.setFilterValue}
                defaultValue={
                  (column.getFilterValue() as string | undefined) ?? null!
                }
              >
                <SelectTrigger
                  value={column.getFilterValue() as string}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={null!}>All</SelectItem>
                  {Array.from(column.getFacetedUniqueValues().keys())
                    .filter(t => t !== undefined)
                    .map(t => (
                      <SelectItem key={t} value={t}>
                        {optionsTranslationPath === undefined ? (
                          t
                        ) : (
                          <FormattedMessage
                            id={
                              `${optionsTranslationPath}.${t}` as TranslationKeys
                            }
                          />
                        )}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
